import { useState, useEffect } from 'react';
import { AuthState, useIdentify } from '@hungryroot/customer';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { Icon, IconVariant } from '@hungryroot/icons';
import { Loading } from '@hungryroot/loading-component-primitives';
import { FutureOrdersWillChangeModal } from '@hungryroot/page-food-profile';
import { Switch } from '@hungryroot/pho';
import { Dietary, DietaryRestrictions, EatingHabit } from '@hungryroot/quiz';
import { Paragraph, Text, Heading, Button } from '@hungryroot/ramen';
import { type DietaryRestrictions as ICustomerDietaryRestrictions } from '@hungryroot/rest';
import { useSubscriptionWarning } from '@hungryroot/supplements';
import { usePatchCustomerDietaryRestrictions } from '../../../../api/v2/customers/[id]/dietary_restrictions/get/patch-dietary-restrictions';
import DietaryPopover from '../DietaryPopover/DietaryPopover';
import { kebabToSentence, kebabToPascal } from './utils';
import styles from './DietaryNeeds.module.css';

interface Props {
  testid?: string;
  onSubtextChange: (
    prefs: DietaryPrefs[],
    options: typeof DIETARY_PREFERENCES_OPTIONS,
    stateKey: string
  ) => void;
}

export enum DietaryPrefs {
  Omnivore = 'is_omnivore',
  Flexitarian = 'is_flexitarian',
  Pescatarian = 'is_pescatarian',
  Vegetarian = 'is_vegetarian',
  Vegan = 'is_vegan',
  DairyFree = 'is_dairy_free',
  GlutenFree = 'is_gluten_free',
  SoyFree = 'is_soy_free',
  TreeNutFree = 'is_nut_free',
  PeanutFree = 'is_peanut_free',
  EggFree = 'is_egg_free',
  ShellfishFree = 'is_shellfish_free',
  SesameFree = 'is_sesame_free',
  GrainFree = 'is_grain_free',
  GarlicFree = 'is_garlic_free',
  OnionFree = 'is_onion_free',
  CaffeineFree = 'is_caffeine_free',
}

/**
 * `optionText` is used for display in the section subtitle if the user has that dietary preference selected.
 */
export const DIETARY_PREFERENCES_OPTIONS = {
  is_vegan: { slug: EatingHabit.Vegan, optionText: 'VEGAN' },
  is_vegetarian: { slug: EatingHabit.Vegetarian, optionText: 'VEGETARIAN' },
  is_pescatarian: { slug: EatingHabit.Pescatarian, optionText: 'PESCATARIAN' },
  is_flexitarian: { slug: EatingHabit.Flexitarian, optionText: 'FLEXITARIAN' },
  is_omnivore: { slug: EatingHabit.Omnivore, optionText: 'OMNIVORE' },
  is_dairy_free: { slug: Dietary.DairyFree, optionText: 'DAIRY-FREE' },
  is_gluten_free: { slug: Dietary.GlutenFree, optionText: 'GLUTEN-FREE' },
  is_soy_free: { slug: Dietary.SoyFree, optionText: 'SOY-FREE' },
  is_nut_free: { slug: Dietary.TreeNutFree, optionText: 'TREE NUT-FREE' },
  is_peanut_free: { slug: Dietary.PeanutFree, optionText: 'PEANUT-FREE' },
  is_egg_free: { slug: Dietary.EggFree, optionText: 'EGG-FREE' },
  is_shellfish_free: {
    slug: Dietary.ShellfishFree,
    optionText: 'SHELLFISH-FREE',
  },
  is_sesame_free: { slug: Dietary.SesameFree, optionText: 'SESAME-FREE' },
  is_grain_free: { slug: Dietary.GrainFree, optionText: 'GRAIN-FREE' },
  is_garlic_free: { slug: Dietary.GarlicFree, optionText: 'GARLIC-FREE' },
  is_onion_free: { slug: Dietary.OnionFree, optionText: 'ONION-FREE' },
  is_caffeine_free: { slug: Dietary.CaffeineFree, optionText: 'CAFFEINE-FREE' },
};

function formatCustomerDietaryRestrictions(
  dietaryRestrictions: ICustomerDietaryRestrictions | undefined
) {
  let habits = EatingHabit.Omnivore;
  const dietary: Dietary[] = [];
  dietaryRestrictions?.dietary_restrictions?.map((dietaryRestriction) => {
    if (!dietaryRestriction.selected) {
      return null;
    }
    if (
      Object.values(EatingHabit).includes(
        dietaryRestriction.slug as EatingHabit
      )
    ) {
      habits = dietaryRestriction.slug as EatingHabit;
    } else if (
      Object.values(Dietary).includes(dietaryRestriction.slug as Dietary)
    ) {
      dietary.push(dietaryRestriction.slug as Dietary);
    }
  });
  return { habits, dietary };
}

export default function DietaryNeeds(props: Props) {
  const { testid, onSubtextChange } = props;
  const customerQuery = useIdentify();
  const { customerData, authState } = customerQuery;
  const {
    data: dietaryRestrictionsData,
    isLoading: isDietaryRestrictionsLoading,
    status: dietaryRestrictionStatus,
  } = useCustomerDietaryRestrictions();
  const [buttonState, setButtonState] = useState({
    loading: false,
    disabled: true,
  });
  const [customerPrefs, setCustomerPrefs] = useState<{
    activeListItem: EatingHabit;
    activeToggles: Dietary[];
  }>({ activeListItem: '' as EatingHabit, activeToggles: [] });
  const [showDialog, setShowDialog] = useState(false);

  const subscriptionWarningCopy = useSubscriptionWarning();

  const patchCustomerDietaryRestrictionsMutation =
    usePatchCustomerDietaryRestrictions();

  useEffect(() => {
    if (dietaryRestrictionStatus !== 'success') {
      return;
    }
    const { dietary, habits } =
      formatCustomerDietaryRestrictions(dietaryRestrictionsData) || {};
    setCustomerPrefs({ activeListItem: habits, activeToggles: dietary });
    setSubtext(habits, dietary);
  }, [dietaryRestrictionsData, dietaryRestrictionStatus]);

  if (authState === AuthState.isLoading || isDietaryRestrictionsLoading) {
    return <Loading />;
  }

  if (customerData === undefined) {
    return null;
  }

  function setSubtext(habit: EatingHabit, dietary: Dietary[]) {
    const activePrefs = [habit, ...dietary];
    const prefs = Object.keys(DIETARY_PREFERENCES_OPTIONS).filter((key) => {
      const slug =
        DIETARY_PREFERENCES_OPTIONS[
          key as keyof typeof DIETARY_PREFERENCES_OPTIONS
        ].slug;

      // Dietary restriction exists in list of DIETARY_PREFERENCES_OPTIONS
      return activePrefs.includes(slug);
    }) as DietaryPrefs[];

    onSubtextChange(
      prefs.length ? prefs : [DietaryPrefs.Omnivore],
      DIETARY_PREFERENCES_OPTIONS,
      'dietaryPrefsText'
    );
  }

  function dropdownClickHandler(item: EatingHabit) {
    setButtonState({ ...buttonState, disabled: false });
    setCustomerPrefs({ ...customerPrefs, activeListItem: item });
    setSubtext(item, customerPrefs.activeToggles);
  }

  const toggleDialog = () => setShowDialog((prev) => !prev);

  function handleConfirmUpdate() {
    toggleDialog();

    if (!customerData?.id) {
      return;
    }

    const activeDietaryRestrictions = [
      ...customerPrefs.activeToggles,
      customerPrefs.activeListItem,
    ];
    const removed = dietaryRestrictionsData?.dietary_restrictions
      .filter((obj) => {
        return !activeDietaryRestrictions.includes(obj.slug as EatingHabit);
      })
      .map((obj) => {
        return { id: obj.id, selected: false };
      });
    const active = activeDietaryRestrictions.map((item) => {
      return {
        id: DietaryRestrictions[
          kebabToPascal(item) as keyof typeof DietaryRestrictions
        ],
        selected: true,
      };
    });

    const data = {
      dietary_restrictions: [...active, ...(removed || [])],
    };
    setButtonState({ ...buttonState, loading: true });

    patchCustomerDietaryRestrictionsMutation.mutate(
      { payload: data },
      {
        onSuccess: () => {
          setButtonState({ loading: false, disabled: true });
        },
      }
    );
  }

  const dietaryRestrictions = Object.keys(Dietary) as Array<
    keyof typeof Dietary
  >;

  const dietaryToggles = dietaryRestrictions.map((key) => {
    const isActive = customerPrefs.activeToggles.includes(Dietary[key]);
    function handleToggleChange() {
      setButtonState({ ...buttonState, disabled: false });
      const activeToggles = isActive
        ? customerPrefs.activeToggles.filter((item) => item !== Dietary[key])
        : [...customerPrefs.activeToggles, Dietary[key]];
      setCustomerPrefs({ ...customerPrefs, activeToggles });
      setSubtext(customerPrefs.activeListItem, activeToggles);
    }
    return (
      <div className={styles.toggle} key={Dietary[key]}>
        <Switch
          icon={<Icon variant={IconVariant[key]} />}
          testid={`${testid}-${Dietary[key as keyof typeof Dietary]}`}
          label={kebabToSentence(Dietary[key])}
          enabled={isActive}
          onChange={handleToggleChange}
        />
      </div>
    );
  });

  return (
    <div data-testid={testid}>
      <div>
        <div className={styles['heading-primary']}>
          <Heading variant="H5">Your diet</Heading>
          <Text>(required)</Text>
        </div>
        <DietaryPopover
          testid={`${testid}-popover`}
          onClick={dropdownClickHandler}
          active={customerPrefs.activeListItem as EatingHabit}
        />
      </div>
      <div>
        <Heading variant="H5" className={styles['heading-secondary']}>
          Dietary needs
        </Heading>
        <Paragraph>
          Select any that apply and we'll be sure to only send you foods that
          fit your needs.
          <a
            href="https://support.hungryroot.com/hc/en-us/articles/360035619354-What-if-I-have-food-allergies-or-intolerances?_gl=1*lz8om6*_ga*MTQyMTU4NTEyMi4xNjUxMjQ3Mjg0*_ga_LH4B8FYSQ2*MTY2MzAxMDExMC4yNDQuMS4xNjYzMDExMTA3LjAuMC4w"
            className={styles.tooltip}
          >
            <Icon variant={IconVariant.Question} />
          </a>
        </Paragraph>
      </div>
      <div className={styles.toggles}>{dietaryToggles}</div>
      <Button
        style="muted"
        prominence="primary"
        size="medium"
        buttonType="submit"
        onClick={toggleDialog}
        textPrimary="Update preferences"
        testid={`${testid}-update-button`}
        width="full-width"
        disabled={buttonState.disabled}
        loading={buttonState.loading}
      />
      {showDialog ? (
        <FutureOrdersWillChangeModal
          subscriptionWarningCopy={subscriptionWarningCopy}
          testid={testid}
          toggleDialog={toggleDialog}
          onConfirmUpdate={handleConfirmUpdate}
        />
      ) : null}
    </div>
  );
}
